import { CORE_SCHEMA, dump as stringifyYaml, load as parseYaml } from "js-yaml";
import { userFacingError } from "../userFacingError";

export const SKILL_MD_PATH = "SKILL.md";
const SKILL_FILE_BASE64_SENTINEL = "3B-SKILL-FILE-BASE64-V1";
const MAX_SKILL_FILE_BYTES = 5 * 1024 * 1024;
export const MAX_SKILL_BUNDLE_BYTES = 25 * 1024 * 1024;
export const MAX_SKILL_FILES = 500;
export const MAX_SKILL_NAME_LENGTH = 64;

const SKILL_NAME = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

export function skillNameError(name: string): string | null {
  if (name.length === 0) return "Enter a name";
  if (name.length > MAX_SKILL_NAME_LENGTH) {
    return `Use at most ${MAX_SKILL_NAME_LENGTH} characters`;
  }
  if (!SKILL_NAME.test(name)) {
    return "Use only lowercase letters, numbers, and single hyphens";
  }
  return null;
}

export function uniqueSkillName(
  base: string,
  taken: ReadonlySet<string>
): string {
  if (!taken.has(base)) return base;
  for (let suffix = 2; ; suffix++) {
    const ending = `-${suffix}`;
    const stem = base
      .slice(0, MAX_SKILL_NAME_LENGTH - ending.length)
      .replace(/-+$/, "");
    const candidate = `${stem}${ending}`;
    if (!taken.has(candidate)) return candidate;
  }
}

type SkillManifest = {
  name: string;
  description: string;
  license?: string;
  compatibility?: string;
  metadata?: Record<string, string>;
  allowedTools?: string;
};

export type ParsedSkill = {
  manifest: SkillManifest;
  instructions: string;
};

export type StoredSkillFile = {
  content: string;
  encoding: "utf8" | "base64";
};

function asRecord(value: unknown): Record<string, unknown> {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    throw userFacingError("SKILL.md frontmatter must be a YAML mapping");
  }
  return value as Record<string, unknown>;
}

function requiredString(
  attrs: Record<string, unknown>,
  key: string,
  max: number
): string {
  const value = attrs[key];
  if (typeof value !== "string" || value.length === 0) {
    throw userFacingError(`SKILL.md ${key} must be a non-empty string`);
  }
  if (value.length > max) {
    throw userFacingError(`SKILL.md ${key} must be at most ${max} characters`);
  }
  return value;
}

function optionalString(
  attrs: Record<string, unknown>,
  key: string,
  max?: number
): string | undefined {
  const value = attrs[key];
  if (value === undefined) return undefined;
  if (typeof value !== "string" || value.length === 0) {
    throw userFacingError(`SKILL.md ${key} must be a non-empty string`);
  }
  if (max !== undefined && value.length > max) {
    throw userFacingError(`SKILL.md ${key} must be at most ${max} characters`);
  }
  return value;
}

function parseMetadata(value: unknown): Record<string, string> | undefined {
  if (value === undefined) return undefined;
  const raw = asRecord(value);
  const metadata: Record<string, string> = {};
  for (const [key, item] of Object.entries(raw)) {
    if (typeof item !== "string") {
      throw userFacingError("SKILL.md metadata values must be strings");
    }
    metadata[key] = item;
  }
  return metadata;
}

function splitSkillMd(raw: string): { yaml: string; body: string } {
  if (!raw.startsWith("---\n")) {
    throw userFacingError("SKILL.md must start with YAML frontmatter");
  }
  const end = raw.indexOf("\n---", 4);
  if (end === -1) throw userFacingError("SKILL.md frontmatter is not closed");
  const after = end + 4;
  if (raw[after] !== undefined && raw[after] !== "\n") {
    throw userFacingError(
      "SKILL.md frontmatter delimiter must be on its own line"
    );
  }
  return {
    yaml: raw.slice(4, end),
    body: raw.slice(after + (raw[after] === "\n" ? 1 : 0)),
  };
}

export function parseSkillMd(raw: string, parentName?: string): ParsedSkill {
  const { yaml, body } = splitSkillMd(raw.replaceAll("\r\n", "\n"));
  let attrs: Record<string, unknown>;
  try {
    attrs = asRecord(parseYaml(yaml, { schema: CORE_SCHEMA }));
  } catch (error) {
    const message = error instanceof Error ? error.message : "Invalid YAML";
    throw userFacingError(`Invalid SKILL.md frontmatter: ${message}`);
  }

  const name = requiredString(attrs, "name", MAX_SKILL_NAME_LENGTH);
  if (!SKILL_NAME.test(name)) {
    throw userFacingError(
      "SKILL.md name must contain only lowercase letters, numbers, and single hyphens"
    );
  }
  if (parentName !== undefined && name !== parentName) {
    throw userFacingError("SKILL.md name must match its parent directory");
  }

  const description = requiredString(attrs, "description", 1024);
  const license = optionalString(attrs, "license");
  const compatibility = optionalString(attrs, "compatibility", 500);
  const allowedTools = optionalString(attrs, "allowed-tools");
  const metadata = parseMetadata(attrs.metadata);

  return {
    manifest: {
      name,
      description,
      ...(license && { license }),
      ...(compatibility && { compatibility }),
      ...(metadata && { metadata }),
      ...(allowedTools && { allowedTools }),
    },
    instructions: body.trim(),
  };
}

export function composeSkillMd({
  manifest,
  instructions,
}: ParsedSkill): string {
  const attrs = {
    name: manifest.name,
    description: manifest.description,
    ...(manifest.license && { license: manifest.license }),
    ...(manifest.compatibility && {
      compatibility: manifest.compatibility,
    }),
    ...(manifest.metadata && { metadata: manifest.metadata }),
    ...(manifest.allowedTools && { "allowed-tools": manifest.allowedTools }),
  };
  return `---\n${stringifyYaml(attrs, { schema: CORE_SCHEMA }).trimEnd()}\n---\n\n${instructions}`;
}

export function validateSkillPath(path: string): string {
  if (
    path.length === 0 ||
    path.length > 512 ||
    path.startsWith("/") ||
    path.endsWith("/") ||
    path.includes("\\") ||
    path.includes("\0")
  ) {
    throw userFacingError("Invalid skill file path");
  }
  const parts = path.split("/");
  if (parts.some((part) => part === "" || part === "." || part === "..")) {
    throw userFacingError("Invalid skill file path");
  }
  return path;
}

const NON_BASE64 = /[^A-Za-z0-9+/]/;

function isBase64(content: string): boolean {
  if (content.length % 4 !== 0) return false;
  const padding = content.endsWith("==") ? 2 : content.endsWith("=") ? 1 : 0;
  return !NON_BASE64.test(content.slice(0, content.length - padding));
}

export function serializeStoredSkillFile(file: StoredSkillFile): string {
  if (file.encoding === "utf8") {
    if (file.content.startsWith(`${SKILL_FILE_BASE64_SENTINEL}\n`)) {
      throw userFacingError("Skill file content uses a reserved marker");
    }
    return file.content;
  }
  if (!isBase64(file.content)) {
    throw userFacingError("Invalid base64 skill file content");
  }
  return `${SKILL_FILE_BASE64_SENTINEL}\n${file.content}`;
}

export function parseStoredSkillFile(content: string): StoredSkillFile {
  const prefix = `${SKILL_FILE_BASE64_SENTINEL}\n`;
  return content.startsWith(prefix)
    ? { content: content.slice(prefix.length), encoding: "base64" }
    : { content, encoding: "utf8" };
}

export function skillFileDecodedBytes(file: StoredSkillFile): number {
  if (file.encoding === "utf8")
    return new TextEncoder().encode(file.content).length;
  const padding = file.content.endsWith("==")
    ? 2
    : file.content.endsWith("=")
      ? 1
      : 0;
  return Math.floor((file.content.length * 3) / 4) - padding;
}

export function validateStoredSkillFile(path: string, file: StoredSkillFile) {
  validateSkillPath(path);
  serializeStoredSkillFile(file);
  if (skillFileDecodedBytes(file) > MAX_SKILL_FILE_BYTES) {
    throw userFacingError(`Skill file ${path} exceeds the file size limit`);
  }
}

export function validateSkillBundle(files: Record<string, StoredSkillFile>) {
  const paths = Object.keys(files);
  if (paths.length > MAX_SKILL_FILES) {
    throw userFacingError(
      `A skill may contain at most ${MAX_SKILL_FILES} files`
    );
  }
  if (!files[SKILL_MD_PATH]) throw userFacingError("Skill is missing SKILL.md");
  if (files[SKILL_MD_PATH].encoding !== "utf8") {
    throw userFacingError("SKILL.md must be UTF-8 text");
  }

  let total = 0;
  for (const [path, file] of Object.entries(files)) {
    validateStoredSkillFile(path, file);
    const bytes = skillFileDecodedBytes(file);
    total += bytes;
  }
  if (total > MAX_SKILL_BUNDLE_BYTES) {
    throw userFacingError("Skill bundle exceeds the total size limit");
  }

  return parseSkillMd(files[SKILL_MD_PATH].content);
}
